import Page from '@/components/Layout/Page';
import React from 'react';
import { MyUserDataFragment, useGetMeQuery } from '@/generated/codegen';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { filterObjByArray } from '@/utils/filterInput';
import { UPDATE_ME_INPUT } from './MyUserSchema';
import { useUpdateMyUserMutation } from '@/generated/codegen';
import { useAuthActions } from '@/utils/auth';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Auth } from '@aws-amplify/auth';
import FormikGotoErrorOnSubmitEffectFn from '@/components/Inputs/FormikGotoErrorOnSubmitEffect';
import { PhoneInputField } from '@/components/Inputs/PhoneInput';
import { makeDefaultValues } from '@/utils/makeDefaultValues';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { makeFormFieldCreator } from '@/components/Inputs/FieldCreator';
import { ConnectedSingleDatePicker } from '@/components/Inputs/SingleDatePicker';
import { ConnectedSelect } from '@/components/Inputs/ConnectedSelect';
import { FieldError } from '@/components/ui/field';
import { FormFields } from '@/components/Inputs/FormFields';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { isMobileApp } from '@/utils/isMobileApp';
import { captureIdentityClaim } from '@/utils/analytics/identityClaimEvents';
import { IDENTITY_CLAIM_ROUTE } from '@/modules/identity_claim/identityClaimConstants';

const userFormSchema = Yup.object().shape({
  first_name: Yup.string().required('Campo obligatorio'),
  last_name: Yup.string().required('Campo obligatorio'),
  email: Yup.string().email('Ingresa un email valido').required('Required'),
  phone: Yup.string().nullable().phoneNumberStrict(),
});
const passwordFormSchema = Yup.object().shape({
  old_password: Yup.string().required('Campo obligatorio'),
  new_password: Yup.string().required('Campo obligatorio'),
  pass_confirm: Yup.string()
    .oneOf([Yup.ref('new_password'), null], 'Las contraseñas no coinciden.')
    .required('Campo obligatorio'),
});

const passwordFieldsCreator = makeFormFieldCreator<{
  old_password: string;
  new_password: string;
  pass_confirm: string;
}>();

const passwordFormFields = [
  passwordFieldsCreator({
    name: 'old_password',
    label: 'Contraseña Actual',
    inputProps: { type: 'password', autoComplete: 'current-password' },
  }),
  passwordFieldsCreator({
    name: 'new_password',
    label: 'Nueva Contraseña',
    inputProps: { type: 'password', autoComplete: 'new-password' },
  }),
  passwordFieldsCreator({
    name: 'pass_confirm',
    label: 'Confirma La Nueva Contraseña',
    inputProps: { type: 'password', autoComplete: 'new-password' },
  }),
];

const userFieldsCreator = makeFormFieldCreator<MyUserDataFragment>();

const userFormFields = [
  userFieldsCreator({
    name: 'first_name',
    label: 'Nombre',
  }),
  userFieldsCreator({
    name: 'last_name',
    label: 'Apellido',
  }),
  userFieldsCreator({
    name: 'dni',
    label: 'DNI',
    disabled: true,
  }),
  userFieldsCreator({
    name: 'birthday',
    label: 'Fecha de Nacimiento',
    inputComponent: ConnectedSingleDatePicker,
    inputProps: {
      default: null,
      pickerProps: {
        disabled: { after: new Date() },
      },
    },
    default: null,
  }),
  userFieldsCreator({
    name: 'email',
    disabled: true,
  }),
  userFieldsCreator({
    name: 'phone',
    label: 'Teléfono',
    inputComponent: () => (
      <Field name="phone" as={PhoneInputField} className="w-full border-none" />
    ),
  }),
  userFieldsCreator({
    name: 'address',
    label: 'Direccion',
  }),
  userFieldsCreator({
    name: 'cuil',
    label: 'CUIL',
  }),
  userFieldsCreator({
    name: 'gender',
    label: 'Sexo',
    inputComponent: ConnectedSelect,
    inputProps: {
      placeholder: 'Seleccioná una opción',
      options: [
        { label: 'Masculino', value: 'male' },
        { label: 'Femenino', value: 'female' },
      ],
    },
  }),
];

const userInitialValues = makeDefaultValues(userFormFields);
const passwordInitialValues = makeDefaultValues(passwordFormFields);

const ProfilePage = () => {
  const { data } = useGetMeQuery();
  const [updateMe, { error: updateMeError }] = useUpdateMyUserMutation();
  const { refreshUser } = useAuthActions();
  const history = useHistory();
  const user = React.useMemo(
    () =>
      data?.getMe && 'item' in data.getMe
        ? data?.getMe?.item
        : userInitialValues,
    [data?.getMe]
  );

  return (
    <Page>
      <div className="flex flex-col gap-6">
        {user.is_managed && isMobileApp() && (
          <Card>
            <CardHeader>
              <CardTitle>Hacé tuya tu cuenta</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <p className="text-sm">
                Si te olvidás la clave o cambiás de compañía, la seguís usando.
                Son 3 pasos.
              </p>
              <Button
                type="button"
                onClick={() => {
                  captureIdentityClaim('identity_claim_started', {
                    entry: 'profile',
                  });
                  history.push(IDENTITY_CLAIM_ROUTE);
                }}
              >
                Empezar
              </Button>
            </CardContent>
          </Card>
        )}
        <Formik
          initialValues={{
            ...user,
            birthday: user.birthday || null,
          }}
          enableReinitialize
          validationSchema={userFormSchema}
          onSubmit={(values, { setSubmitting, setStatus }) => {
            let input = filterObjByArray(UPDATE_ME_INPUT, values);
            input.birthday = values.birthday
              ? (format(new Date(values.birthday), 'yyyy-MM-dd') as any)
              : null;

            updateMe({ variables: { input } })
              .then(() => {
                refreshUser();
              })
              .then(() => {
                setSubmitting(false);
              })
              .catch((e) => {
                setStatus({ error: e });
                setSubmitting(false);
              });
          }}
        >
          {({ status, dirty, isSubmitting }) => {
            return (
              <Form>
                <Card>
                  <CardHeader>
                    <CardTitle>Usuario</CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-4">
                    <FormFields fields={userFormFields} />
                    <span style={{ color: 'red' }}>
                      {status?.error?.message}
                      {updateMeError?.message}
                    </span>
                  </CardContent>
                  <CardFooter>
                    <Button
                      variant="success"
                      className="block"
                      disabled={isSubmitting || !dirty}
                      type="submit"
                    >
                      {isSubmitting && <LoadingSpinner />}
                      {!isSubmitting && 'Actualizar'}
                    </Button>
                  </CardFooter>
                </Card>
                <FormikGotoErrorOnSubmitEffectFn />
              </Form>
            );
          }}
        </Formik>
        <Formik
          initialValues={passwordInitialValues}
          enableReinitialize
          validationSchema={passwordFormSchema}
          onSubmit={(values, { setSubmitting, setStatus }) => {
            setStatus({});
            Auth.currentAuthenticatedUser()
              .then((user) => {
                return Auth.changePassword(
                  user,
                  values.old_password,
                  values.new_password
                );
              })
              .then(() => {
                setStatus({
                  success: 'Contraseña actualizada con exito',
                });
                setSubmitting(false);
              })
              .catch((e) => {
                setStatus({ error: e });
                setSubmitting(false);
              });
          }}
        >
          {({ status, dirty, isSubmitting }) => (
            <Form>
              <Card>
                <CardHeader>
                  <CardTitle>Cambia tu Contraseña</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">
                  <FormFields fields={passwordFormFields} />
                  {status?.error?.message ||
                    (updateMeError?.message && (
                      <FieldError className="block!">
                        {status?.error?.message}
                        {updateMeError?.message}
                      </FieldError>
                    ))}
                  <span style={{ color: 'green' }}>{status?.success}</span>
                </CardContent>
                <CardFooter>
                  <Button
                    variant="success"
                    className="block"
                    disabled={isSubmitting || !dirty}
                    type="submit"
                  >
                    {isSubmitting && <LoadingSpinner />}
                    {!isSubmitting && 'Actualizar'}
                  </Button>
                </CardFooter>
              </Card>
            </Form>
          )}
        </Formik>
      </div>
    </Page>
  );
};

export default ProfilePage;
